import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { ArrowUp, Plus } from "lucide-react";
import { AppHeaderBar, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { peekSkeletonComposerText, saveSkeletonComposerText } from "./newSessionSkeletonState";
import { NEW_SESSION_MASCOT_SIZE_CLASS, NEW_SESSION_SCROLL_AREA_CLASS, NEW_SESSION_WELCOME_COLUMN_CLASS } from "./newSessionLayout";
import { PROMPT_PILL_ROW_CLASS, PromptPillSkeleton, LEAD_STARTER_PILL_LAYOUT_CLASS, STARTER_PILL_ROW_CLASS, StarterPromptPillSkeleton } from "./promptPills";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
import { assetUrl } from "@/lib/assetUrl";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { cn } from "@repo/blacksmith-ui/cn";
import { CENTERED_LOADING_ANVIL_SIZE_CLASS, CENTERED_LOADING_ANVIL_SIZE_PX } from "@/components/agent-session/centeredLoadingAnvil";

/* ---------------------------------------------------------------------------
 * Structural skeletons for the Codesmith session view.
 *
 * SessionLoadingScreen    – centered animated anvil for existing sessions.
 *                           Used inside Codesmith.tsx where the real sidebar
 *                           is already rendered alongside.
 *
 * NewSessionChatSkeleton  – the new-session welcome layout (centered mascot,
 *                           greeting, composer, starter rows).
 *
 * SessionSkeleton         – sidebar + main pane. Used in the page-level
 *                           Suspense fallback where the entire Codesmith view
 *                           is replaced; picks the main-pane variant from the
 *                           current route.
 * -------------------------------------------------------------------------*/

/** Loading state for an existing session. */
export function SessionLoadingScreen() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="status" aria-label="Loading session" className="flex min-w-0 flex-1 items-center justify-center bg-[var(--color-app-background)]"><span aria-hidden={true} className={cn(CENTERED_LOADING_ANVIL_SIZE_CLASS, "flex items-center justify-center")}><img src={assetUrl("anvil-animations/anvil-ruminating-light.png")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="select-none object-contain [image-rendering:pixelated] dark:hidden" /><img src={assetUrl("anvil-animations/anvil-ruminating-dark.png")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="hidden select-none object-contain [image-rendering:pixelated] dark:block" /></span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * `NewSessionView` mounts PrActionCards only when `pr_preview` hydrates a
 * `previewManagedPr` (see Codesmith.tsx) — the "View in Codesmith" deep link
 * carries the PR metadata as JSON in that param. A bare `pr` param (a
 * resolve-pr landing with no metadata, or a `repo`+`pr` Fix-with-Codesmith
 * link) resolves into TaskStarterCards instead, so only `pr_preview` reserves
 * the PrActionCards skeleton shape (four action pills).
 */
function hasPrPreviewInSearch(search: string): boolean {
  return new URLSearchParams(search).has("pr_preview");
}

/** Placeholder pill count matching PrActionCards' four action pills. */
const PR_ACTION_PILL_COUNT = 4;

/**
 * Mirrors NewSessionView's non-compact layout so the fallback resolves into
 * the real page without reshaping. The mascot and composer are static chrome
 * rendered for real; the user-dependent greeting and starter rows shimmer.
 * The composer is a live textarea whose text hands off
 * to the real composer's draft (see newSessionSkeletonState), so the user
 * can start typing before the app finishes loading.
 */
export function NewSessionChatSkeleton() {
  const $ = _c(31);
  const isHydrated = useIsHydrated();
  let t0;
  if ($[0] !== isHydrated) {
    t0 = isHydrated && hasPrPreviewInSearch(window.location.search);
    $[0] = isHydrated;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isPrAttached = t0;
  let t1;
  if ($[2] !== isHydrated) {
    t1 = isHydrated ? peekSkeletonComposerText() ?? "" : "";
    $[2] = isHydrated;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const seededText = t1;
  let t2;
  if ($[4] !== isHydrated) {
    t2 = isHydrated && !readLeftSidebarSkeletonLayout().visible && <PanelTogglePlaceholder />;
    $[4] = isHydrated;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <AppHeaderBar inset="canvas" className="bg-app-background">{t2}</AppHeaderBar>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="relative mt-1"><img src={assetUrl("anvil-hello-light.png")} alt="[code]smith" className={`${NEW_SESSION_MASCOT_SIZE_CLASS} dark:hidden`} /><img src={assetUrl("anvil-hello-dark.png")} alt="[code]smith" className={`hidden ${NEW_SESSION_MASCOT_SIZE_CLASS} dark:block`} /></div>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex flex-col items-center">{t4}<div className="flex h-[25px] items-center"><Skeleton className="h-5 w-72 max-w-full rounded" /></div></div>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isHydrated || $[11] !== seededText) {
    t6 = el => {
      if (!el) {
        return;
      }
      const handoff = peekSkeletonComposerText() ?? "";
      if (isHydrated && handoff !== seededText) {
        el.value = handoff;
      }
      el.setSelectionRange(el.value.length, el.value.length);
    };
    $[10] = isHydrated;
    $[11] = seededText;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== seededText || $[14] !== t6) {
    t7 = <textarea ref={t6} rows={1} autoFocus={true} autoComplete="off" data-1p-ignore={true} spellCheck={false} aria-label="Chat input" placeholder="Got a task or question?" defaultValue={seededText} onChange={_temp} className="type-chat-prose pointer-events-auto w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70" />;
    $[13] = seededText;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute inset-y-0 left-0 flex items-center gap-1"><span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary"><Plus className="size-[18px]" strokeWidth={2} aria-hidden={true} /></span></div>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="absolute inset-y-0 right-0 flex items-center"><span className="flex h-7 w-7 items-center justify-center rounded-full border border-black/[0.08] bg-gradient-to-r from-[var(--brand-gradient-blue-tertiary)] to-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500"><ArrowUp className="size-3.5" strokeWidth={2.5} aria-hidden={true} /></span></div>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7) {
    t10 = <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]"><div className="relative flex min-h-8 items-center">{t7}{t8}{t9}</div></div>;
    $[18] = t7;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = isPrAttached ? PROMPT_PILL_ROW_CLASS : STARTER_PILL_ROW_CLASS;
  let t12;
  if ($[20] !== isPrAttached) {
    t12 = isPrAttached ? Array.from({
      length: PR_ACTION_PILL_COUNT
    }, _temp2) : <><StarterPromptPillSkeleton className={LEAD_STARTER_PILL_LAYOUT_CLASS} /><StarterPromptPillSkeleton className="grow" /></>;
    $[20] = isPrAttached;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t12) {
    t13 = <div className="flex w-full flex-col gap-4"><div className={t11}>{t12}</div></div>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t10 || $[26] !== t13) {
    t14 = <div className={NEW_SESSION_SCROLL_AREA_CLASS}><div className={`relative mb-auto flex max-w-full flex-col px-4 ${NEW_SESSION_WELCOME_COLUMN_CLASS}`}>{t5}<div className="flex w-full flex-col gap-3">{t10}{t13}</div></div></div>;
    $[25] = t10;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t3) {
    t15 = <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t3}{t14}</div>;
    $[28] = t14;
    $[29] = t3;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}

/**
 * `/[org]/sessions` and `/[org]/sessions/new` land on NewSessionView, so the
 * fallback should be welcome-shaped there rather than chat-transcript-shaped.
 */
function _temp2(_, i) {
  return <PromptPillSkeleton key={i} className="w-32" />;
}
function _temp(e) {
  return saveSkeletonComposerText(e.target.value);
}
function isNewSessionPath(path: string): boolean {
  const segments = path.split(/[?#]/)[0].split("/").filter(Boolean);
  return segments[1] === "sessions" && (segments.length === 2 || segments[2] === "new");
}

/** Full skeleton (sidebar + main pane) for the page-level Suspense fallback. */
export function SessionSkeleton() {
  const $ = _c(7);
  const router = useRouter();
  const surface = useCodesmithSurface(router.query.embed);
  const isHydrated = useIsHydrated();
  const path = isHydrated ? window.location.pathname : router.asPath;
  const t0 = surface !== "extension";
  let t1;
  if ($[0] !== t0) {
    t1 = <SidebarSkeleton showSearch={t0} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== path) {
    t2 = isNewSessionPath(path) ? <NewSessionChatSkeleton /> : <SessionLoadingScreen />;
    $[2] = path;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="pointer-events-none flex h-full">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}